import { Injectable } from '@angular/core';
import { orderBy, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  Club,
  LicenseType,
  LicenseUserRate,
  Optional,
  PlayerBasicInfo,
  PlayerInscriptionRequest,
  PlayerInscriptionResponse,
  User,
  UserRegistrationFindRequest,
  UserRole,
} from '@models';
import {
  combineLatest,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { ClubsService } from './club.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { TenantContextService } from './tenant-context.service';

/**
 * Season-aware federation status returned by `checkIfUserIsRegistered` (R86–R88).
 * OPTIONAL on the response so old consumers keep working (retrocompatible).
 */
export interface FederationStatus {
  /** R87 — true if the person has ANY active license in the current season. */
  hasActiveLicenseThisSeason: boolean;
  /** R88 — license types active in the current season. */
  activeLicenseTypesThisSeason: LicenseType[];
  /** Season used for the computation (server-resolved). */
  seasonYear: string;
}

/**
 * Ampliated response of `checkIfUserIsRegistered` (R86). Retrocompatible:
 * - Case 1 (does not exist): `{ redirect: true, federation }`.
 * - Case 2 (exists without auth): `{ ...user, federation }`.
 * - Case 3 (exists with auth): `{ existsWithAuth: true, federation }`
 *   (replaces the bare `null` previously returned).
 *
 * `federation` is optional so any pre-deploy/legacy response is still typed.
 */
export type CheckIfUserIsRegisteredResponse = Partial<
  User & { redirect: boolean; existsWithAuth: boolean }
> & { federation?: FederationStatus };

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  public users$!: Observable<User[]>;
  public all$!: Observable<(User & { club?: Club })[]>;
  public licenses$!: Observable<LicenseUserRate[]>;
  public allWithLicenses$!: Observable<
    (User & { club?: Club; licenses?: LicenseUserRate[] })[]
  >;
  public allUsersEmail$!: Observable<
    (User & {
      club?: Club;
      isClubManager: boolean;
      isSchoolManager: boolean;
      isRefereeCoordinator: boolean;
      licenseTypes: LicenseType[];
    })[]
  >;
  public allUsersNotifications$!: Observable<
    (User & {
      club?: Club;
      isClubManager: boolean;
      isSchoolManager: boolean;
      isRefereeCoordinator: boolean;
      licenseTypes: LicenseType[];
    })[]
  >;

  constructor(
    private firestore: FirestoreService,
    private clubsService: ClubsService,
    private functions: Functions,
    private seasonManager: SeasonManagerService,
    private tenantContext: TenantContextService,
  ) {
    this.users$ = this.tenantContext.tenantId$.pipe(
      switchMap(() =>
        this.firestore.listDocuments<User>(
          this.tenantContext.collections.USERS,
        ),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.all$ = combineLatest([this.users$, this.clubsService.all$]).pipe(
      map(([users, clubs]) =>
        users.map((user) => ({
          ...user,
          club: user.clubId
            ? clubs.find((c) => c.id === user.clubId)
            : undefined,
        })),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.licenses$ = this.seasonManager.season$.pipe(
      switchMap((seasonYear) =>
        this.firestore.listDocumentsGroup<LicenseUserRate>(
          'user_licenses',
          where('tenantId', '==', this.tenantContext.tenantId),
          where('seasonYear', '==', seasonYear),
        ),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.allWithLicenses$ = combineLatest([this.all$, this.licenses$]).pipe(
      map(([users, licenses]) =>
        users.map((user) => ({
          ...user,
          licenses: licenses.filter((l) => l.userId === user.id),
        })),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    const enrichUsers = (
      users: (User & { club?: Club })[],
      licenses: LicenseUserRate[],
    ) =>
      users.map((user) => {
        const isClubManager =
          (user.roles?.includes(UserRole.MANAGER) ?? false) && !!user.clubId;
        const isSchoolManager = user.club?.schoolManagerId === user.id;
        const isRefereeCoordinator =
          user.roles?.includes(UserRole.REFEREE_COORDINATOR) ?? false;
        const licenseTypes: LicenseType[] = licenses
          .filter((l) => l.userId === user.id)
          .map((l) => l.licenseType);
        return { ...user, isClubManager, isSchoolManager, isRefereeCoordinator, licenseTypes };
      });

    this.allUsersEmail$ = combineLatest([this.all$, this.licenses$]).pipe(
      map(([users, licenses]) => enrichUsers(users, licenses)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.allUsersNotifications$ = combineLatest([
      this.all$,
      this.licenses$,
    ]).pipe(
      map(([users, licenses]) =>
        enrichUsers(users.filter((u) => u.fcmToken), licenses),
      ),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  private get col() {
    return this.tenantContext.collections.USERS;
  }

  /**
   * Per-id cache of the single-user Observable. The `user` pipe is IMPURE, so its
   * `transform(userId)` runs on EVERY change-detection cycle and re-subscribes the
   * `async` pipe whenever the returned Observable reference changes. Returning a
   * fresh `getDocument()` stream each call therefore caused an infinite re-read
   * loop on any view rendering `refereeId | user | async` (match cards). Caching a
   * `shareReplay`-backed Observable per id keeps the reference stable across CD
   * cycles, killing the loop and deduplicating reads of the same user app-wide.
   */
  private readonly userByIdCache = new Map<string, Observable<User>>();

  get(id: string): Observable<User> {
    const cached = this.userByIdCache.get(id);
    if (cached) return cached;
    const obs = this.firestore.getDocument<User>(this.col, id).pipe(
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    this.userByIdCache.set(id, obs);
    return obs;
  }

  getReferees() {
    return this.seasonManager.season$.pipe(
      switchMap((seasonYear) =>
        this.firestore.listDocumentsGroup<LicenseUserRate>(
          'user_licenses',
          where('tenantId', '==', this.tenantContext.tenantId),
          where('seasonYear', '==', seasonYear),
          where('licenseType', '==', 'REFEREE'),
        ),
      ),
      switchMap((licenses) => {
        const userIds = licenses.map((l) => l.userId);
        if (userIds.length === 0) return of([]);
        const chunkArray = (arr: string[], size: number) => {
          const result: string[][] = [];
          for (let i = 0; i < arr.length; i += size)
            result.push(arr.slice(i, size + i));
          return result;
        };
        return combineLatest(
          chunkArray(userIds, 30).map((chunk) =>
            this.firestore.listDocuments<User>(
              this.col,
              where('id', 'in', chunk),
            ),
          ),
        ).pipe(map((results) => results.flat()));
      }),
      shareReplay(1),
    );
  }

  getPlayers(clubId: string): Observable<User[]> {
    return combineLatest([
      this.firestore.listDocuments(
        this.tenantContext.collections.TEAMS,
        where('clubId', '==', clubId),
      ),
      this.users$,
    ]).pipe(
      map(([teams, users]) => {
        const playerIds = new Set(
          (teams as any[]).flatMap((t) => t.playersIds ?? []),
        );
        return users.filter((u) => playerIds.has(u.id));
      }),
    );
  }

  getDelegates(clubId: string): Observable<User[]> {
    return combineLatest([
      this.firestore.listDocuments(
        this.tenantContext.collections.TEAMS,
        where('clubId', '==', clubId),
      ),
      this.users$,
    ]).pipe(
      map(([teams, users]) => {
        const delegateIds = new Set(
          (teams as any[]).flatMap((t) => t.delegatesIds ?? []),
        );
        return users.filter((u) => delegateIds.has(u.id));
      }),
    );
  }

  getReport() {
    return this.firestore.listDocuments<User>(this.col);
  }

  getUsersByClubId(clubId: string): Observable<(User & { club?: Club })[]> {
    return this.all$.pipe(
      map((users) => users.filter((u) => u.clubId === clubId)),
    );
  }

  findPlayer(data: PlayerInscriptionRequest) {
    const callable = httpsCallable<
      PlayerInscriptionRequest,
      PlayerInscriptionResponse
    >(this.functions, 'findPlayer');
    return callable(data);
  }

  createOrUpdate(user: Optional<User, 'id'>) {
    user.document = user.document.toUpperCase();
    if (user.id) {
      return this.update(user.id, user).then(() =>
        this.syncUserClaims(user.id!).catch((err: unknown) => {
          // Suppress only "not an admin" permission errors (e.g. non-admin users calling this).
          // Propagate meaningful validation errors such as "last admin" or "self-demotion" protection.
          const code = (err as any)?.code as string | undefined;
          if (code === 'functions/permission-denied') return;
          throw err;
        }),
      );
    }
    return this.createUser(user);
  }

  private createUser(user: Omit<User, 'id'>) {
    return this.firestore.create<User>(this.col, user);
  }

  update(id: string, user: Partial<User>) {
    return this.firestore.update<User>(this.col, { id, ...user });
  }

  syncUserClaims(userId: string) {
    const callable = httpsCallable<{ userId: string }, { success: boolean }>(
      this.functions,
      'syncUserClaims',
    );
    return callable({ userId });
  }

  updateUserEmail(userId: string, newEmail: string) {
    const callable = httpsCallable<
      { userId: string; newEmail: string },
      { success: boolean }
    >(this.functions, 'updateUserEmail');
    return callable({ userId, newEmail });
  }

  createUserFromAdminPanel(data: Omit<User, 'id'>) {
    const callable = httpsCallable<Omit<User, 'id'>, string>(
      this.functions,
      'createUserFromAdminPanel',
    );
    return callable(data);
  }

  /**
   * Ensures a manager (gestor) associated to a club has a Firebase Auth account
   * so they can log in. Idempotent: creates the account (uid = doc id) when it
   * doesn't exist yet — e.g. imported users with no Auth — and (re)sets MANAGER
   * claims for the active tenant.
   */
  ensureManagerAuth(userId: string) {
    const callable = httpsCallable<
      { userId: string },
      { success: boolean; created: boolean }
    >(this.functions, 'ensureManagerAuth');
    return callable({ userId });
  }

  checkIfUserIsRegistered(data: Omit<UserRegistrationFindRequest, 'tenantId'>) {
    const callable = httpsCallable<
      UserRegistrationFindRequest,
      CheckIfUserIsRegisteredResponse
    >(this.functions, 'checkIfUserIsRegistered');
    return callable({ ...data, tenantId: this.tenantContext.tenantId });
  }

  /**
   * PRE-LOGIN account creation for the federate flow. The callable is PUBLIC and
   * derives nothing from `request.auth` (the user has no session yet), so we send
   * `tenantId` explicitly — exactly like `checkIfUserIsRegistered`. The Cloud
   * Function creates the Firebase Auth account, writes the tenant user doc and
   * sets the MEMBERSHIP CLAIMS (`tenants: { [tenantId]: PLAYER }`) needed by both
   * `payLicense` and the cross-tenant guard.
   */
  async createUserAndAuth(user: Partial<User> & { password?: string }) {
    const callable = httpsCallable<
      Partial<User> & { tenantId: string; password?: string },
      { uid: string }
    >(this.functions, 'createUserAndAuth');
    return callable({ ...user, tenantId: this.tenantContext.tenantId });
  }

  /**
   * Alta de un jugador/delegado durante la inscripción. La creación y los
   * duplicados se resuelven en la Cloud Function con Admin SDK, porque bajo las
   * security rules ni el gestor (móvil) ni el admin impersonado (rules
   * multi-tenant) pueden escribir/consultar `users` de forma fiable.
   */
  createInscriptionPlayer(user: Partial<User> & { clubId: string }) {
    const callable = httpsCallable<
      Partial<User> & { clubId: string; tenantId: string },
      | { success: true; userId: string }
      | { success: false; duplicated: 'document' | 'phone' | 'email' }
    >(this.functions, 'createInscriptionPlayer');
    return callable({ ...user, tenantId: this.tenantContext.tenantId });
  }

  async checkIfDuplicatedDocument(document: string) {
    return firstValueFrom(
      this.firestore.findDocument<User>(
        this.col,
        where('document', '==', document.toUpperCase()),
      ),
    );
  }

  async checkIfDuplicatedPhone(phone: string) {
    return firstValueFrom(
      this.firestore.findDocument<User>(this.col, where('phone', '==', phone)),
    );
  }

  async checkIfDuplicatedEmail(email: string) {
    return firstValueFrom(
      this.firestore.findDocument<User>(this.col, where('email', '==', email)),
    );
  }

  getUserLicenses(userId: string) {
    return this.firestore.listDocumentsGroup<LicenseUserRate>(
      'user_licenses',
      where('tenantId', '==', this.tenantContext.tenantId),
      where('userId', '==', userId),
      orderBy('seasonYear', 'desc'),
    );
  }

  getPlayerPublicInfo(userId: string) {
    // Endpoint público ("seguir el deporte" sin estar federado): pasamos el
    // tenantId explícito porque sin sesión no hay token del que derivarlo.
    const callable = httpsCallable<
      { userId: string; tenantId: string | null },
      Partial<User>
    >(this.functions, 'getPlayerPublicInfo');
    return callable({ userId, tenantId: this.tenantContext.tenantIdOrNull });
  }

  /**
   * Batch, PII-free display info (name + photo) for many players at once.
   * Public endpoint — powers "follow the sport" screens (team roster, player
   * ranking) without requiring a session.
   */
  async getPlayersBasicInfo(userIds: string[]): Promise<PlayerBasicInfo[]> {
    if (userIds.length === 0) return [];
    const callable = httpsCallable<
      { userIds: string[]; tenantId: string | null },
      PlayerBasicInfo[]
    >(this.functions, 'getPlayersBasicInfo');
    const res = await callable({
      userIds,
      tenantId: this.tenantContext.tenantIdOrNull,
    });
    return res.data;
  }

  resetEveryUser() {
    const callable = httpsCallable<
      void,
      { message: string; updatedUsers: number }
    >(this.functions, 'onResetUsers');
    return callable();
  }
}
